import { useQueryClient } from '@tanstack/react-query';
import { FC, Fragment, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';

import { Controls, ControlsButton } from '@/components/Controls';
import PulsingLoader, {
  LoaderHolder,
  LoaderTextHolder,
  LoaderTitle,
} from '@/components/PulsingLoader';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useDidUpdate from '@/hooks/useDidUpdate';
import { MLProjectScriptPart, MLProjectTranslation } from '@/types/resources';
import { api } from '@/utils/api';

import {
  ColumnTitle,
  Editor,
  Row,
  ScriptColumn,
  TimestampsColumn,
} from './ScriptsCard';

const StyledControls = styled(Controls)`
  display: inline-flex;
  margin: 24px 0 12px;
`;

const TypeLabel = styled(Text)`
  margin-bottom: 16px;
`;

const Translations = styled.div<{ $pushBottom?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${({ $pushBottom = false }) =>
    $pushBottom &&
    css`
      padding-bottom: 30px;
    `}
`;

const TranslationRow = styled.div`
  display: flex;
  align-items: flex-start;
`;

const TranslationVariation = styled(Text)`
  flex-shrink: 0;
  margin-right: 24px;
  display: flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
`;

const TranslationText = styled(Text)`
  font-weight: 600;
  flex: 1;
`;

const DeleteButton = styled.button`
  width: 92px;
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  position: absolute;
  cursor: pointer;
  top: 24px;
  right: 24px;
  transition: 0.2s ease-in-out border-color;

  svg {
    margin-right: 12px;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.lightGray};
  }
`;

interface Props {
  translations: MLProjectTranslation[];
  script?: MLProjectScriptPart[];
}

const ScriptTranslationsPreview: FC<Props> = ({ translations, script }) => {
  const theme = useTheme();
  const alerts = useAlerts();
  const queryClient = useQueryClient();

  const [activeTranslation, setActiveTranslation] =
    useState<MLProjectTranslation>(translations[0]!);

  const renderRows = () => {
    return script?.map((original, i) => {
      const translation = activeTranslation.script?.[i];

      return (
        <Fragment key={i}>
          <Row>
            <TimestampsColumn>
              <Text $type="caption" $color="gray">
                {original.timestamp}
              </Text>
            </TimestampsColumn>
            <ScriptColumn>
              <TypeLabel $type="smallBold" $color="gray">
                Original
              </TypeLabel>
              <Text $type="body">{original.text}</Text>
            </ScriptColumn>
          </Row>
          <Row>
            <TimestampsColumn>
              <Text $type="caption" $color="gray">
                {translation?.timestamp ?? '-'}
              </Text>
            </TimestampsColumn>
            <ScriptColumn>
              <TypeLabel $type="smallBold" $color="gray">
                Translations
              </TypeLabel>
              <Translations $pushBottom={i === script?.length - 1}>
                <TranslationRow>
                  <TranslationVariation $type="smallHeadline">
                    A
                  </TranslationVariation>
                  <TranslationText $type="body">
                    {translation?.text?.a ?? '-'}
                  </TranslationText>
                </TranslationRow>
                <TranslationRow>
                  <TranslationVariation $type="smallHeadline">
                    B
                  </TranslationVariation>
                  <TranslationText $type="body">
                    {translation?.text?.b ?? '-'}
                  </TranslationText>
                </TranslationRow>
              </Translations>
            </ScriptColumn>
          </Row>
        </Fragment>
      );
    });
  };

  const onDelete = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure?',
        `Delete ${activeTranslation.language} translation`,
      );

      if (!confirmed) {
        return;
      }

      await api.delete(
        `/v1/distribution/ml-translations/${activeTranslation.id}`,
      );

      await queryClient.invalidateQueries(['ml_projects']);

      alerts.close();
    } catch {
      alerts.error();
    }
  };

  useDidUpdate(() => {
    setActiveTranslation(translations[0]!);
  }, [translations]);

  return (
    <>
      <Text $type="caption">Generated Translations</Text>
      <StyledControls>
        {translations.map((translation) => (
          <ControlsButton
            key={translation.id}
            active={activeTranslation.id === translation.id}
            onClick={() => setActiveTranslation(translation)}
          >
            {translation.language}
          </ControlsButton>
        ))}
      </StyledControls>
      <Editor>
        <DeleteButton onClick={onDelete}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="24" height="24" rx="4" fill={theme.colors.grayscale} />
            <path
              d="M16.9497 8.13095L7.04968 8.13096"
              stroke={theme.colors.gray}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10.65 10.9167V14.6309"
              stroke={theme.colors.gray}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13.35 10.9167V14.6309"
              stroke={theme.colors.gray}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M16.0497 8.13095V16.9524C16.0497 17.0755 16.0023 17.1936 15.9179 17.2807C15.8335 17.3677 15.7191 17.4167 15.5997 17.4167H8.39971C8.28036 17.4167 8.1659 17.3677 8.08151 17.2807C7.99712 17.1936 7.94971 17.0755 7.94971 16.9524V8.13095"
              stroke={theme.colors.gray}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M14.25 8.13095V7.20238C14.25 6.9561 14.1552 6.71992 13.9864 6.54578C13.8176 6.37164 13.5887 6.2738 13.35 6.2738H10.65C10.4113 6.2738 10.1824 6.37164 10.0136 6.54578C9.84482 6.71992 9.75 6.9561 9.75 7.20238V8.13095"
              stroke={theme.colors.gray}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <Text as="span" $type="smallHeadline">
            Delete
          </Text>
        </DeleteButton>
        {activeTranslation.isGeneratingTranslation ? (
          <LoaderHolder $marginBottom={false}>
            <PulsingLoader />
            <LoaderTextHolder>
              <LoaderTitle $type="button">
                Generating translation ...
              </LoaderTitle>
              <Text $color="gray" $type="captionSemibold">
                Please be patient, this action may take
                <br />a little longer in some cases
              </Text>
            </LoaderTextHolder>
          </LoaderHolder>
        ) : (
          <>
            <Row>
              <TimestampsColumn>
                <ColumnTitle>Timestamps</ColumnTitle>
              </TimestampsColumn>
              <ScriptColumn>
                <ColumnTitle>Script</ColumnTitle>
              </ScriptColumn>
            </Row>
            {renderRows()}
          </>
        )}
      </Editor>
    </>
  );
};

export default ScriptTranslationsPreview;
